import { useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@constants';
import { useDocumentUpload } from '@hooks/documents/useDocumentUpload';
import { UploadCallbacks } from '@libs/types';
import { extractFileExtension } from '@libs/utils';

interface UseChatDocumentUploadParams {
  conversationId: string | undefined;
}

export interface ChatUploadCallbacks extends Omit<UploadCallbacks, 'onSuccess'> {
  onSuccess: (documentId: string, uploadMeta?: { s3Key: string; mimeType: string }) => void;
}

/**
 * Hook for managing chat document uploads
 * Handles the full upload lifecycle: presigned URL creation and S3 upload
 * Note: Chat documents are stateless (no database persistence or confirmation step)
 *
 * @param conversationId - The ID of the conversation
 * @returns Upload functions and state management utilities
 */
export const useChatDocumentUpload = ({
  conversationId,
}: UseChatDocumentUploadParams) => {
  const queryClient = useQueryClient();

  const baseHook = useDocumentUpload({
    endpoints: {
      createUrl: conversationId
        ? `api/chat/conversations/${conversationId}/documents/create-upload-url`
        : '', // Disabled if no conversation
      confirm: undefined, // Chat doesn't need confirm endpoint (stateless)
      delete: (documentId: string, metadata) => {
        if (!conversationId) {
          throw new Error('Conversation ID is required for document deletion');
        }

        if (!metadata) {
          throw new Error('Metadata is required for chat document deletion');
        }

        // Extract file extension from metadata (required for S3 key construction)
        const ext = extractFileExtension(metadata.fileName as string);
        if (!ext) {
          throw new Error(`Cannot extract file extension from: ${metadata.fileName}`);
        }

        return `chat/documents/${documentId}?conversationId=${conversationId}&fileExtension=${ext}`;
      },
    },
    documentCategory: 'chat',
  });

  /**
   * Upload a document to a chat conversation
   *
   * @param file - The file to upload
   * @param callbacks - Callbacks for upload lifecycle events (onStart, onProgress, onSuccess, onError)
   * @returns Upload ID for tracking and cancellation
   */
  const uploadDocument = async (file: File, callbacks: ChatUploadCallbacks) => {
    // Wrap the onSuccess callback to add cache invalidation and expose s3Key + mimeType
    const wrappedCallbacks: UploadCallbacks = {
      ...callbacks,
      onSuccess: (documentId, uploadMeta) => {
        // Invalidate conversation messages to trigger refetch
        if (conversationId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.komplyanceAi.messages(conversationId),
          });
        }

        // Call with full metadata so caller can build the document reference
        callbacks.onSuccess(documentId as string, {
          s3Key: uploadMeta?.s3Key ?? '',
          mimeType: file.type,
        });
      },
    };

    return baseHook.uploadDocument(
      file,
      { fileName: file.name }, // Pass fileName in metadata for delete endpoint
      wrappedCallbacks,
    );
  };

  return {
    ...baseHook,
    uploadDocument,
  };
};
